import React, { useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { Paper, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { TableCellWithWidth } from "components/list/TableComponentsStyled";
import {
  BALANCE_SHEETS_QUERY,
  ACCSTATEMENT_CONFIGS_QUERY,
  BALANCE_SHEET_TYPES,
  FINALIZATION_TYPES
} from "./queries";
import { SAVE_PARTNER_BALANCE_SHEET, REMOVE_PARTNER_BALANCE_SHEET } from "./mutations";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { useQueryWithSimpleErrorHandling } from 'hooks/apolloWrappers';
import BalanceSheetListRowComponent from "./BalanceSheetListRowComponent";
import { getCurrencies } from "../../selectors";
import { loadCurrencies } from "../../actionCreators";
import { useSelector, useDispatch } from "react-redux";
import { update, assocPath, omit, evolve, always } from "ramda";
import AddIcon from "@mui/icons-material/Add";
import { Button, Card, FakeText, useToast, Typography } from "@totalsoft/rocket-ui";
import { concatFailure, required, shape, errorMessage, unique, items } from "@totalsoft/pure-validations";
import { useDirtyFieldValidation, getErrors } from "@totalsoft/pure-validations-react";
import { withParams } from "../../../../providers/ParamsConsumer";
import { useChangeTrackingState } from "@totalsoft/change-tracking-react";
import { useSimpleError } from "hooks/apolloWrappers";

const balanceSheetValidator = concatFailure(
  unique(x => x.localName || {}, "BalanceSheet.Name"),
  items(
    shape({
      localName: concatFailure(required |> errorMessage("BalanceSheet.NameRequired")),
      configId: required |> errorMessage("BalanceSheet.ConfigurationRequired"),
      balanceSheetTypeId: required |> errorMessage("BalanceSheet.TypeRequired"),
      dimensionYear: required |> errorMessage("BalanceSheet.YearRequired"),
      dimensionMonth: required |> errorMessage("BalanceSheet.MonthRequired"),
      currencyId: required |> errorMessage("BalanceSheet.CurrencyRequired"),
      currencyRate: required |> errorMessage("BalanceSheet.CurrencyRateRequired"),
      finalizationTypeId: required |> errorMessage("BalanceSheet.FinalizedTypeRequired")
    })
  )
);

const BalanceSheetContainer = ({ partnerId, params }) => {
  const [t] = useTranslation();
  const [balanceSheetList, dirtyInfo, setBalanceSheetList, resetDirtyInfo] = useChangeTrackingState([]);
  const currencies = useSelector(getCurrencies);
  const dispatch = useDispatch();
  const showError = useSimpleError();
  const showToast = useToast();
  const [validation, validate, resetValidation] = useDirtyFieldValidation(balanceSheetValidator);
  const defaultCurrencyCode = params.defaultCurrencyCode;

  useEffect(() => {
    dispatch(loadCurrencies());
  }, [dispatch]);

  useEffect(() => {
    validate(balanceSheetList, dirtyInfo);
  }, [balanceSheetList, dirtyInfo, dispatch, validate, validation]);

  const { loading, data } = useQueryWithSimpleErrorHandling(BALANCE_SHEETS_QUERY, {
    variables: { partnerId },
    onCompleted: data => {
      const localBalanceSheetList =
        data?.partnerBalanceSheets?.map(item => ({ ...item, inEdit: false, isSaving: false })) || [];
      setBalanceSheetList(localBalanceSheetList);
      resetValidation();
      resetDirtyInfo();
    }
  });

  const { data: accStatementConfigsData } = useQueryWithSimpleErrorHandling(ACCSTATEMENT_CONFIGS_QUERY);
  const accStatementConfigOptions = useMemo(() => 
    accStatementConfigsData?.accStatementConfigs.map(x => ({ configId: x.configId, configName: x.configName, bankId: x.bankId })) || [], [accStatementConfigsData?.accStatementConfigs]);

  const { data: balanceSheetTypesData } = useQueryWithSimpleErrorHandling(BALANCE_SHEET_TYPES);
  const balanceSheetTypes = useMemo(
    () =>
      balanceSheetTypesData?.balanceSheetTypes.map(x => ({
        dictionaryItemId: x.dictionaryItemId,
        dictionaryItemName: x.dictionaryItemName
      })) || [],
    [balanceSheetTypesData?.balanceSheetTypes]
  );

  const { data: finalizationTypesData } = useQueryWithSimpleErrorHandling(FINALIZATION_TYPES);
  const finalizationTypes = useMemo(
    () =>
      finalizationTypesData?.finalizationTypes.map(x => ({
        dictionaryItemId: x.dictionaryItemId,
        dictionaryItemName: x.dictionaryItemName
      })) || [],
    [finalizationTypesData?.finalizationTypes]
  );


  const [savePartnerBalanceSheet] = useMutation(SAVE_PARTNER_BALANCE_SHEET, {
    onError: error => {
      showError(error);
      setBalanceSheetList(prev => prev.map(x => ({ ...x, isSaving: false })));
    },
    update: cache => {
      showToast(t("BalanceSheet.BalanceSheetSaved"), "success", { autoClose: 3000 });
      cache.evict({ id: "ROOT_QUERY", fieldName: "partnerBalanceSheets" });
      cache.gc();
    }
  });

  const [removePartnerBalanceSheet] = useMutation(REMOVE_PARTNER_BALANCE_SHEET, {
    onError: error => {
      showError(error);
      setBalanceSheetList(prev => prev.map(x => ({ ...x, isSaving: false })));
    },
    update: cache => {
      showToast(t("BalanceSheet.BalanceSheetRemoved"), "success", { autoClose: 3000 });
      cache.evict({ id: "ROOT_QUERY", fieldName: "partnerBalanceSheets" });
      cache.gc();
    }
  });

  const handleSave = useCallback(
    (item, _) => {
      if (!validate(balanceSheetList)) return;

      setBalanceSheetList(prev =>
        prev.map(x => (x.balanceSheetId === item.balanceSheetId ? { ...x, isSaving: true } : x))
      );
      savePartnerBalanceSheet({
        variables: {
          input: omit(["dimensionId", "inEdit", "isSaving", "finalizationType", "balanceSheetType", "bankId"], item)
        }
      });
    },
    [balanceSheetList, savePartnerBalanceSheet, setBalanceSheetList, validate]
  );

  const handleDelete = useCallback(
    balanceSheetId => {
      if (!balanceSheetId) setBalanceSheetList(prev => prev.filter(x => x.balanceSheetId));
      else {
        setBalanceSheetList(prev =>
          prev.map(x => (x.balanceSheetId === balanceSheetId ? { ...x, isSaving: true } : x))
        );
        removePartnerBalanceSheet({
          variables: {
            balanceSheetId: balanceSheetId
          }
        });
      }
    },
    [removePartnerBalanceSheet, setBalanceSheetList]
  );

  const handleChange = useCallback(
    (prop, value, index, id) => {
      switch (prop) {
        case !value && "inEdit": {
          const original = data?.partnerBalanceSheets?.find(x => x.balanceSheetId === id);
          setBalanceSheetList(prev => update(index, {...original, inEdit: false, isSaving: false }, prev));
          return;
        }
        case "dimension": {
          const newDate = new Date(value);
          setBalanceSheetList(prev =>
            update(index, evolve({
              dimensionYear: always(newDate.getFullYear()),
              dimensionMonth: always(newDate.getMonth() + 1)
            },
            prev[index]), prev));
          return;
        }
        default: {
          setBalanceSheetList(prev => update(index, assocPath([prop], value, prev[index]), prev));
          if (value && prop === "inEdit") resetDirtyInfo();
        }
      }
    },
    [data?.partnerBalanceSheets, resetDirtyInfo, setBalanceSheetList]
  );

  const handleAdd = useCallback(() => {
    const currencyId = currencies.find(x => x.code === defaultCurrencyCode)?.currencyId;
    setBalanceSheetList(prev => [
      ...prev,
      {
        balanceSheetId: null,
        partnerId: partnerId,
        configId: null,
        bankId: null,
        balanceSheetType: null,
        dimensionYear: new Date().getFullYear(),
        dimensionMonth: new Date().getMonth(),
        currencyId: currencyId,
        currencyRate: 1,
        finalizationType: null,
        inEdit: true
      }
    ]);
  }, [currencies, defaultCurrencyCode, partnerId, setBalanceSheetList]);

  const isEditing = useMemo(() => balanceSheetList.some(x => x.inEdit === true), [balanceSheetList]);

  return (
    <Card title={t("BalanceSheet.Title")} subheader={<Typography color="error">{validation |> getErrors}</Typography>}>
      {loading ? (
        <FakeText lines={10} />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCellWithWidth w={12}>{t("BalanceSheet.Name")}</TableCellWithWidth>
                <TableCellWithWidth w={15}>{t("BalanceSheet.Configuration")}</TableCellWithWidth>
                <TableCellWithWidth w={12}>{t("BalanceSheet.Type")}</TableCellWithWidth>
                <TableCellWithWidth w={15}>{t("BalanceSheet.DateMonth")}</TableCellWithWidth>
                <TableCellWithWidth w={10}>{t("BalanceSheet.Currency")}</TableCellWithWidth>
                <TableCellWithWidth w={12}>{t("BalanceSheet.CurrencyRate")}</TableCellWithWidth>
                <TableCellWithWidth w={12}>{t("BalanceSheet.FinalizedType")}</TableCellWithWidth>
                <TableCellWithWidth w={10} align="right">
                  <Button
                    id="BalanceSheet_Add"
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                    sx={{ whiteSpace: "nowrap" }}
                    disabled={isEditing}
                  >
                    {t("Add")}
                  </Button>
                </TableCellWithWidth>
              </TableRow>
            </TableHead>
            <TableBody>
              {balanceSheetList.map((item, index) => (
                <BalanceSheetListRowComponent
                  key={`balanceSheetId_${item.balanceSheetId}_index_${index}`}
                  item={item}
                  index={index}
                  accStatementConfigOptions={accStatementConfigOptions}
                  currencies={currencies}
                  finalizationTypes={finalizationTypes}
                  balanceSheetTypes={balanceSheetTypes}
                  onChange={handleChange}
                  onSave={handleSave}
                  onDelete={handleDelete}
                  isEditing={isEditing}
                  validation={validation}
                  dirtyInfo={dirtyInfo}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Card>
  );
};

BalanceSheetContainer.propTypes = {
  partnerId: PropTypes.number,
  params: PropTypes.object.isRequired
};

export default withParams(BalanceSheetContainer);
